import { ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import useErrors from '../../../../../hooks/useErrors';
import useApiCall from '../../../../../hooks/useApiCall';
import { toast } from 'react-toastify';
import { ProgramAttachmentItem, ProgramBeingEditedType } from '../../types';
import activitiesService from '../../../../../services/activitiesService';
import { mapUrlsToAttachmentItems, resolveAttachmentsToFiles } from '../../utils/attachments';

interface UseEditModalI {
  loadPrograms: () => Promise<void>;
  setEditModalShow: Dispatch<SetStateAction<boolean>>;
  editModalShow: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  programBeingEditted: ProgramBeingEditedType;
}

export default function useEditModal({ setIsLoading, loadPrograms, setEditModalShow, programBeingEditted, editModalShow }: UseEditModalI) {
  const [specificProgram, setSpecificProgram] = useState('');
  const [attachments, setAttachments] = useState<ProgramAttachmentItem[]>([]);

  const {
    setError, removeError, getErrorMessageByFieldName, errors,
  } = useErrors();

  const { apiCall } = useApiCall();

  function handleSpecificProgramChange(event: ChangeEvent<HTMLTextAreaElement>) {
    setSpecificProgram(event.target.value);
    if (!event.target.value) {
      setError({ field: 'specificProgram', message: 'Descrição da programação é obrigatória!' });
    } else {
      removeError('specificProgram');
    }
  }

  const editProgram = useCallback(async () => {
    setIsLoading(true);

    try {
      // Substituição total: só entram no FormData os arquivos que devem permanecer/ficar.
      // Anexos existentes mantidos precisam ser reenviados como File (não URL).
      const files = await resolveAttachmentsToFiles(attachments);

      await apiCall({
        apiToCall: activitiesService.updateActivityProgram,
        onStartLoad: () => { setIsLoading(true); },
        queryParams: { id: programBeingEditted.id },
        reqBody: [
          { key: 'specificOrientation', value: specificProgram },
          ...files.map((file) => ({ key: 'attachments', value: file })),
        ],
        actionAfterResponse: (response) => {
          if (response.success) {
            toast.success('Edição da programação enviada para aprovação com sucesso!');
            setEditModalShow(false);
            setSpecificProgram('');
            setAttachments([]);
            loadPrograms();
            return;
          }
          toast.error('Não foi possível editar a programação. Por favor, tente novamente');
          setIsLoading(false);
        },
        catchMessage: 'Não foi possível editar a programação. O upload dos anexos pode ter falhado. Por favor, tente novamente',
        catchAction: () => setIsLoading(false),
      });
    } catch (error) {
      console.log(error);
      toast.error('Não foi possível preparar os anexos para envio. Por favor, tente novamente');
      setIsLoading(false);
    }
  }, [apiCall, attachments, loadPrograms, programBeingEditted.id, setEditModalShow, setIsLoading, specificProgram]);

  const isEditFormValid = !!specificProgram && errors.length === 0;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setSpecificProgram(programBeingEditted.specificProgram);
    setAttachments(mapUrlsToAttachmentItems(programBeingEditted.attachments));
  }, [editModalShow]);

  return {
    editProgram,
    specificProgram,
    handleSpecificProgramChange,
    getErrorMessageByFieldName,
    isEditFormValid,
    attachments,
    setAttachments,
  };
}
